import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Package, Calendar, ShieldOff, Loader2, FileText, FileDown } from "lucide-react";

const API_BASE = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

/**
 * /share/:token — Public, no-auth, opaque-token gated.
 * Renders package metadata + sheet IDs. Sheet files themselves remain auth-gated;
 * this portal exists for procurement/sub review of which sheets are included
 * and as a discoverable link surface (audit-tracked).
 */
export default function PackageSharePortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true); setError(null);
    axios.get(`${API_BASE}/share/package/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const code = e?.response?.status;
        if (code === 410) setError("This share link has expired.");
        else if (code === 404) setError("This share link is invalid or has been revoked.");
        else setError(e?.response?.data?.detail || "Unable to load share link.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--fop-bg)] text-[var(--fop-text-3)] text-sm" data-testid="share-portal-loading">
        <Loader2 size={16} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--fop-bg)] p-6">
        <div className="fop-surface-2 rounded-lg p-8 text-center max-w-md border border-[var(--fop-border)]" data-testid="share-portal-error">
          <ShieldOff size={32} className="text-red-400 mx-auto mb-3" />
          <h1 className="font-display text-xl text-[var(--fop-text)] mb-2">Link unavailable</h1>
          <p className="text-sm text-[var(--fop-text-3)] mb-4">{error}</p>
          <Link to="/" className="text-xs font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:underline">ForgedOps</Link>
        </div>
      </div>
    );
  }

  const pkg = data?.package || {};
  const sheetIds = pkg.sheet_ids || [];
  const pdf = data?.package_pdf;

  return (
    <div className="min-h-screen bg-[var(--fop-bg)] text-[var(--fop-text)]" data-testid="share-portal">
      <header className="border-b border-[var(--fop-border)] bg-[var(--fop-surface-2)]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display text-lg tracking-tight text-[var(--fop-text)]">
            Forged<span className="text-[var(--fop-primary)]">Ops</span>
          </Link>
          <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-4)]">Shared Package · Read Only</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6">
        <div className="fop-surface-2 rounded-lg p-6 mb-4 border border-[var(--fop-border)]">
          <div className="flex items-center gap-2 mb-2">
            <Package size={16} className="text-[var(--fop-primary)]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">{pkg.template || "custom"}</span>
          </div>
          <h1 className="font-display text-2xl text-[var(--fop-text)] mb-2">{pkg.name}</h1>
          {pkg.description && <p className="text-sm text-[var(--fop-text-2)] max-w-2xl">{pkg.description}</p>}
          <div className="flex items-center gap-4 mt-4 text-[11px] font-mono text-[var(--fop-text-4)]">
            <span>{sheetIds.length} sheet{sheetIds.length === 1 ? "" : "s"}</span>
            <span className="inline-flex items-center gap-1">
              <Calendar size={11} /> Link expires {fmtDate(data?.expires_at)}
            </span>
          </div>
          {pdf ? (
            <a
              href={`${API_BASE}/share/package/${token}/pdf`}
              data-testid="share-portal-download-pdf"
              className="mt-5 btn btn-primary inline-flex items-center gap-2 text-sm"
              download
            >
              <FileDown size={14} /> Download Package PDF
              <span className="text-[10px] font-mono opacity-80 ml-1">
                · {pdf.page_count || 0} pages · {Math.round((pdf.size_bytes || 0) / 1024)} KB
              </span>
            </a>
          ) : (
            <div className="mt-5 inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[11px] font-mono text-[var(--fop-text-3)]" data-testid="share-portal-no-pdf">
              Package PDF has not been generated yet.
            </div>
          )}
        </div>

        <div className="fop-surface-2 rounded-lg p-5 border border-[var(--fop-border)]" data-testid="share-portal-sheets">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)] mb-3">Sheets in package</div>
          {sheetIds.length === 0 ? (
            <div className="text-sm text-[var(--fop-text-4)] py-6 text-center italic">This package contains no sheets.</div>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {sheetIds.map((id) => (
                <li key={id} className="px-3 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded text-[11px] font-mono text-[var(--fop-text-2)] inline-flex items-center gap-2 truncate">
                  <FileText size={11} className="text-[var(--fop-text-4)] shrink-0" />
                  <span className="truncate">{id}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-[11px] text-[var(--fop-text-4)] italic">
            Sheet files are not embedded in this public link. The recipient must be invited to the project to view drawings.
          </p>
        </div>

        <p className="mt-6 text-center text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-4)]">
          Powered by ForgedOps Plans · <Link to="/legal/privacy" className="hover:text-[var(--fop-text-2)]">Privacy</Link> · <Link to="/legal/terms" className="hover:text-[var(--fop-text-2)]">Terms</Link>
        </p>
      </main>
    </div>
  );
}
